import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoutes from '@/app/routes';
import {
  PWAInstallButton,
  PWAUpdateToast,
  OfflineBanner,
  IOSInstallPrompt,
  MigrationToast,
  NotificationPrompt,
  PushNotificationPrompt,
  SplashScreen,
  StartScreen,
} from '@/components/ui';
import { NetworkAwareRouter } from '@/components/layout';
import { runMigrationIfNeeded } from '@/utils/migration';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { GameProvider } from '@/contexts/GameContext';

// Define the type for the window object with our custom properties
declare global {
  interface Window {
    checkForUpdate: () => boolean;
    applyUpdate: () => void;
  }
}

const App: React.FC = () => {
  // Safe state initialization with error handling
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(true);
  const [showMigrationToast, setShowMigrationToast] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showPushNotificationPrompt, setShowPushNotificationPrompt] = useState(false);
  const [updateApplied, setUpdateApplied] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [showStartScreen, setShowStartScreen] = useState(false);
  const [showAboutPage, setShowAboutPage] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Global error handler to catch any undefined variable errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('🚨 Global error caught:', event.error);
      
      // Only set hasError for critical errors that prevent the app from working
      if (event.error && event.error.message) {
        const errorMessage = event.error.message.toLowerCase();
        
        // Check for critical errors that should crash the app
        if (errorMessage.includes('initialroute') || 
            errorMessage.includes('cannot read property') ||
            errorMessage.includes('is not a function')) {
          console.error('❌ Critical error detected:', event.error);
          setHasError(true);
        } else {
          console.log('⚠️ Non-critical error, continuing:', event.error);
        }
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Check if we need to redirect from /arcade/games/riddl to /arcade/games/riddl/
  useEffect(() => {
    try {
      // Only redirect on desktop to avoid mobile issues
      const userAgent = navigator.userAgent || '';
      const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      
      console.log('🔍 Mobile detection:', { userAgent, isMobile, pathname: window.location.pathname });
      
      // Don't redirect on mobile to avoid potential issues
      if (!isMobile && window.location.pathname === '/arcade/games/riddl') {
        console.log('Redirecting from /arcade/games/riddl to /arcade/games/riddl/');
        window.location.href = '/arcade/games/riddl/';
      }
    } catch (error) {
      console.error('❌ Redirect error:', error);
      // Don't set hasError here as redirect is not critical for app functionality
      console.log('⚠️ Redirect failed, continuing with current path');
    }
  }, []);

  // Run migration on app start
  useEffect(() => {
    try {
      const migratedData = runMigrationIfNeeded();
      if (Object.keys(migratedData.gameState).length > 0) {
        console.log('Migration completed with data:', migratedData);
        setShowMigrationToast(true);
      }
    } catch (error) {
      console.error('Migration failed:', error);
      // Don't set hasError here as migration is not critical for app functionality
      // The app can still work with default state
    }
  }, []);

  useEffect(() => {
    // Check for updates periodically with error handling
    const checkForUpdates = () => {
      try {
        if (!updateApplied && window.checkForUpdate && typeof window.checkForUpdate === 'function') {
          if (window.checkForUpdate()) {
            setShowUpdateToast(true);
          }
        }
      } catch (error) {
        console.error('❌ Update check failed:', error);
        // Don't set hasError here as updates are not critical for app functionality
      }
    };

    // Check immediately and then every 30 seconds
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 30000);

    return () => clearInterval(interval);
  }, [updateApplied]);



  useEffect(() => {
    // Check for notification permission and show prompt if needed
    const checkNotificationPermission = () => {
      try {
        // Check if localStorage is available (some mobile browsers in private mode don't support it)
        if (!('localStorage' in window)) {
          console.log('🔕 localStorage not supported on this device/browser');
          return;
        }
        
        const hasBeenPrompted = localStorage.getItem('riddl-notification-prompted');
        
        // Check if Notification API is available before using it
        if (!('Notification' in window)) {
          console.log('🔕 Notifications not supported on this device/browser');
          return;
        }
        
        const hasPermission = Notification.permission === 'granted';
        
        // Check if app is running as PWA (standalone mode)
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                      ('standalone' in window.navigator && (window.navigator as Navigator & { standalone?: boolean }).standalone === true) ||
                      document.referrer.includes('android-app://');
        
        console.log('🔍 PWA Detection:', {
          displayMode: window.matchMedia('(display-mode: standalone)').matches,
          navigatorStandalone: 'standalone' in window.navigator ? (window.navigator as Navigator & { standalone?: boolean }).standalone : 'not supported',
          referrer: document.referrer,
          isPWA,
          hasBeenPrompted,
          hasPermission,
          notificationsSupported: 'Notification' in window
        });
        
        if (hasBeenPrompted === null && hasPermission === false && 'Notification' in window) {
          // Show notification prompt after a delay for all browsers
          console.log(`📱 Showing notification prompt for ${isPWA ? 'PWA' : 'browser'} user`);
          setTimeout(() => {
            setShowNotificationPrompt(true);
          }, 3000);
        } else if (hasBeenPrompted === null && hasPermission === true && 'Notification' in window) {
          // User already has permission but hasn't been prompted - auto-enable notifications
          console.log('✅ User already has permission - auto-enabling notifications');
          setTimeout(async () => {
            try {
              // Import the notification functions
              const { saveNotificationSettings, scheduleDailyNotifications } = await import('@/utils/dailyNotifications');
              
              // Auto-enable notifications
              saveNotificationSettings({
                enabled: true,
                morningTime: '09:00',
                afternoonTime: '14:00',
                eveningTime: '18:00',
              });
              
              // Schedule notifications
              await scheduleDailyNotifications();
              
              // Mark as prompted
              localStorage.setItem('riddl-notification-prompted', 'enabled');
              
              console.log('✅ Notifications auto-enabled for user with existing permission');
            } catch (error) {
              console.error('Failed to auto-enable notifications:', error);
            }
          }, 1000);
        } else if (hasBeenPrompted === 'disabled') {
          console.log('🚫 Skipping notification prompt - user disabled in settings');
        } else if (hasBeenPrompted === 'enabled' || hasBeenPrompted === 'true') {
          console.log('✅ Notifications already enabled by user');
        } else {
          console.log('🔍 Notification prompt conditions not met:', {
            hasBeenPrompted,
            hasPermission,
            notificationsSupported: 'Notification' in window,
            permission: Notification.permission
          });
        }
      } catch (error) {
        console.error('❌ Error checking notification permissions:', error);
        // Don't set hasError here as notifications are not critical for app functionality
      }
    };

    checkNotificationPermission();
    
    // Check for push notification support and show prompt if needed
    const checkPushNotificationSupport = () => {
      try {
        // Import push notification functions
        import('@/utils/pushNotifications').then(({ isPushSupported, getNotificationPermission }) => {
          if (isPushSupported()) {
            const pushPermission = getNotificationPermission();
            const pushPrompted = localStorage.getItem('riddl-push-prompted');
            
            // Show push notification prompt if:
            // 1. User hasn't been prompted for push notifications
            // 2. User has granted notification permission
            // 3. User hasn't explicitly dismissed push notifications
            if (!pushPrompted && pushPermission === 'granted') {
              console.log('📱 Showing push notification prompt');
              setTimeout(() => {
                setShowPushNotificationPrompt(true);
              }, 5000); // Show after 5 seconds
            }
          }
        }).catch(error => {
          console.error('Failed to load push notification utils:', error);
        });
      } catch (error) {
        console.error('❌ Error checking push notification support:', error);
      }
    };

    checkPushNotificationSupport();
    
  }, []);


  const handleUpdate = () => {
    // Apply the update using the service worker
    console.log('🔄 Applying update...');
    setUpdateApplied(true);
    setShowUpdateToast(false);
    
    try {
      if (window.applyUpdate && typeof window.applyUpdate === 'function') {
        window.applyUpdate();
      } else {
        console.log('⚠️ Update function not available, falling back to page reload');
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ Update failed:', error);
      // Fallback to page reload
      window.location.reload();
    }
  };

  const handleDismissUpdate = () => {
    setShowUpdateToast(false);
  };



  // Debug logging for mobile issues - wrapped in try-catch to prevent crashes
  try {
    console.log('🔍 App State:', { showSplash, showStartScreen, showAboutPage, userAgent: navigator.userAgent });
  } catch (error) {
    console.error('❌ App state logging error:', error);
    // Don't set hasError here as it's just logging
  }

  // Error boundary display
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-red-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-800 mb-4">Something went wrong</h1>
          <p className="text-red-600 mb-4">There was an error loading the app</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reload App
          </button>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      {/* Splash Screen - Shows on app load */}
      {showSplash && (
        <SplashScreen onComplete={() => {
          try {
            console.log('🎬 Splash complete, showing start screen');
            setShowSplash(false);
            setShowStartScreen(true);
          } catch (error) {
            console.error('❌ Splash completion error:', error);
            setHasError(true);
          }
        }} />
      )}
      
      {/* Start Screen - Shows after splash */}
      {showStartScreen && (
        <StartScreen 
          onStart={() => setShowStartScreen(false)} 
          onHowToPlay={() => {
            setShowStartScreen(false);
            setShowAboutPage(true);
          }}
        />
      )}
      
      {/* About Page - Show when How to Play is clicked */}
      {showAboutPage && (
        <div className="min-h-screen bg-gradient-to-br  bg-teal-300 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-black mb-2">How to Play Riddl</h1>
              <p className="text-black text-lg">Master the daily word-guessing challenge</p>
            </div>
            
            {/* Main Content Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-teal-200 dark:border-gray-700">
              <div className="space-y-6">
                {/* Game Overview */}
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Game Rules</h2>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                    Riddl is a daily word-guessing game where you solve riddles to find the answer.
                  </p>
                </div>
                
                {/* Rules List */}
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                      <span className="text-gray-700 dark:text-gray-200">Each day, you'll get a new riddle to solve</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                      <span className="text-gray-700 dark:text-gray-200">You have 4 attempts to guess the correct word</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                      <span className="text-gray-700 dark:text-gray-200">After each guess, you'll see which letters are correct and in the right position</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                      <span className="text-gray-700 dark:text-gray-200">Green letters are correct and in the right spot</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                      <span className="text-gray-700 dark:text-gray-200">Yellow letters are correct but in the wrong spot</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
                      <span className="text-gray-700 dark:text-gray-200">Gray letters are not in the word</span>
                    </li>
                  </ul>
                </div>
                
                {/* Play Button */}
                <div className="text-center pt-4">
                  <button
                    onClick={() => {
                      setShowAboutPage(false);
                      // This will show the game routes since start screen is already hidden
                    }}
                    className="w-40 py-2 px-6 rounded-full border-2 bg-black text-white font-semibold text-lg transition-all duration-200 shadow-lg transform hover:border-transparent focus:border-transparent active:border-transparent focus:outline-none"
                  >
                    Play
                  </button>
                </div>
                
                {/* Test Notification Button - Only show if notifications are enabled */}
                {'Notification' in window && Notification.permission === 'granted' && (
                  <div className="text-center pt-2">
                    <button
                      onClick={() => {
                        new Notification('🧩 Riddl Test', {
                          body: 'Manual test notification - notifications are working!',
                          icon: '/arcade/games/riddl/icons/riddl_icon.png',
                          tag: 'riddl-manual-test'
                        });
                      }}
                      className="w-40 py-2 px-6 rounded-full border-2 border-teal-500 bg-transparent text-teal-500 font-semibold text-lg transition-all duration-200 shadow-lg transform hover:bg-teal-500 hover:text-white focus:outline-none"
                    >
                      Test Notification
                    </button>
                  </div>
                )}
                
                {/* Test Update Button - For testing PWA updates */}
                <div className="text-center pt-2">
                  <button
                    onClick={() => {
                      // Simulate an update being available for testing
                      console.log('🧪 Testing update system...');
                      if (window.checkForUpdate && typeof window.checkForUpdate === 'function') {
                        // Force the update check to return true for testing
                        const originalCheck = window.checkForUpdate;
                        window.checkForUpdate = () => true;
                        
                        // Trigger update check
                        if (window.checkForUpdate()) {
                          setShowUpdateToast(true);
                        }
                        
                        // Restore original function
                        window.checkForUpdate = originalCheck;
                      } else {
                        console.log('⚠️ Update system not available');
                      }
                    }}
                    className="w-40 py-2 px-6 rounded-full border-2 border-orange-500 bg-transparent text-orange-500 font-semibold text-lg transition-all duration-200 shadow-lg transform hover:bg-orange-500 hover:text-white focus:outline-none"
                  >
                    Test Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Game Routes - Only show when all screens are hidden */}
      {!showSplash && !showStartScreen && !showAboutPage && (
        <Router basename="/arcade/games/riddl">
          <NetworkAwareRouter>
            <GameProvider>
              <OfflineBanner />
              <AppRoutes />
              {/* Only show PWA features if service worker is available */}
              {('serviceWorker' in navigator) && <PWAInstallButton />}
              {showIOSPrompt && (
                <IOSInstallPrompt onDismiss={() => setShowIOSPrompt(false)} />
              )}
              {showUpdateToast && (
                <PWAUpdateToast
                  onUpdate={handleUpdate}
                  onDismiss={handleDismissUpdate}
                />
              )}
              {showMigrationToast && (
                <MigrationToast
                  onDismiss={() => setShowMigrationToast(false)}
                />
              )}
              {showNotificationPrompt && (
                <NotificationPrompt onDismiss={() => setShowNotificationPrompt(false)} />
              )}
              {showPushNotificationPrompt && (
                <PushNotificationPrompt onDismiss={() => setShowPushNotificationPrompt(false)} />
              )}
            </GameProvider>
          </NetworkAwareRouter>
        </Router>
      )}
      

    </ThemeProvider>
  );
};

export default App;
